import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { submissionsAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    Send, 
    CheckCircle2,
    Building2,
    Shield,
    Info,
    Globe
} from 'lucide-react';

const US_STATES = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
    "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
    "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
    "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota",
    "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada",
    "New Hampshire", "New Jersey", "New Mexico", "New York",
    "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon",
    "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington",
    "West Virginia", "Wisconsin", "Wyoming", "National"
];

const ORGANIZATION_CATEGORIES = [
    "Employer",
    "Employment Support",
    "Job Training",
    "Mental Health Services",
    "Life Skills Training",
    "Social Programs",
    "Advocacy & Rights",
    "Housing Support",
    "Educational Programs",
    "Recreation & Community",
    "Healthcare Services",
    "Family Support",
    "Government Agency",
    "Transportation",
    "Other"
];

const AddOrganizationPage = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, login } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        // Organization Info
        name: '',
        description: '',
        website: '',
        state: '',
        city: '',
        category: '',
        
        // Organization Contact
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        
        // Services
        services_offered: '',
        target_population: '',
        
        // Additional Info
        how_found: '',
        other_info: '',
    });

    const [errors, setErrors] = useState({});

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Organization name is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.category) newErrors.category = 'Category is required';
        
        // Contact email validation if provided
        if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
            newErrors.contact_email = 'Invalid email format';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validate()) return;
        
        setSubmitting(true);
        
        try {
            // Add submission type and submitter info
            const submissionData = {
                name: formData.name,
                mission: formData.description, // Map to existing field
                website: formData.website,
                state: formData.state,
                city: formData.city,
                stakeholder_type: formData.category,
                contact_name: formData.contact_name,
                contact_email: formData.contact_email,
                contact_phone: formData.contact_phone,
                services_for_employment: formData.services_offered,
                nd_population_served: formData.target_population,
                other_info: `How found: ${formData.how_found}\n\n${formData.other_info}`,
                submission_type: 'directory_addition',
                requester_name: user?.name || 'Member',
                requester_email: user?.email || '',
                requester_role: 'HAAPE Member',
                added_by_member: true,
            };
            
            await submissionsAPI.create(submissionData);
            setSubmitted(true);
            toast.success('Organization has been added to the directory!');
        } catch (error) {
            console.error('Submission error:', error);
            toast.error('Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Login required
    if (!isAuthenticated) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center" data-testid="login-required">
                <Card className="max-w-md w-full mx-4">
                    <CardHeader className="text-center">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                            <Shield className="h-8 w-8 text-primary" />
                        </div>
                        <CardTitle>Member Access Required</CardTitle>
                        <CardDescription>
                            Please log in to add an organization to the directory.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button onClick={login} className="w-full" data-testid="login-btn">
                            Member Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center p-4" data-testid="submission-success">
                <Card className="max-w-md w-full">
                    <CardContent className="pt-8 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="h-8 w-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-bold mb-2">Organization Submitted!</h2>
                        <p className="text-muted-foreground mb-6">
                            Thank you for adding to our directory. Our team will review this submission 
                            and it will appear in the directory once approved.
                        </p>
                        <div className="flex gap-3 justify-center">
                            <Button onClick={() => navigate('/directory')} data-testid="go-directory-btn">
                                View Public Directory
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => { setSubmitted(false); setFormData({
                                    name: '', description: '', website: '', state: '', city: '', category: '',
                                    contact_name: '', contact_email: '', contact_phone: '',
                                    services_offered: '', target_population: '', how_found: '', other_info: ''
                                }); }}
                                data-testid="add-another-btn"
                            >
                                Add Another
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="add-organization-page">
            <div className="container max-w-2xl mx-auto py-8 px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                        <Globe className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold mb-2">Add to the Public Directory</h1>
                    <p className="text-muted-foreground max-w-lg mx-auto">
                        Know an organization that supports the neurodivergent community? 
                        Add them to our public directory to help others find valuable resources.
                        You don't need to know them personally.
                    </p>
                </div>

                <Alert className="mb-6 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 dark:text-blue-200">
                        <strong>This is for the public directory.</strong> Organizations added here will be listed 
                        in our searchable directory but will NOT receive member login access. They don't need to 
                        sign our Terms & Conditions or know about being listed.
                        <br /><br />
                        <strong>Want to nominate a trusted partner for full membership?</strong>{' '}
                        <a href="/nominate" className="text-primary hover:underline font-medium">Use the Nominate a Member form instead →</a>
                    </AlertDescription>
                </Alert>

                <form onSubmit={handleSubmit}>
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Building2 className="h-5 w-5 text-primary" />
                                Organization Information
                            </CardTitle>
                            <CardDescription>Basic information about the organization</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Organization Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                    placeholder="e.g., Autism Services Center"
                                    data-testid="input-name"
                                />
                                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Description *</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => handleChange('description', e.target.value)}
                                    placeholder="What does this organization do? What services do they offer?"
                                    rows={3}
                                    data-testid="input-description"
                                />
                                {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="category">Category *</Label>
                                    <Select 
                                        value={formData.category} 
                                        onValueChange={(v) => handleChange('category', v)}
                                    >
                                        <SelectTrigger data-testid="select-category">
                                            <SelectValue placeholder="Select category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {ORGANIZATION_CATEGORIES.map((cat) => (
                                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.category && <p className="text-sm text-destructive">{errors.category}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="website">Website</Label>
                                    <Input
                                        id="website"
                                        value={formData.website}
                                        onChange={(e) => handleChange('website', e.target.value)}
                                        placeholder="https://example.org"
                                        data-testid="input-website"
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="state">State *</Label>
                                    <Select 
                                        value={formData.state} 
                                        onValueChange={(v) => handleChange('state', v)}
                                    >
                                        <SelectTrigger data-testid="select-state">
                                            <SelectValue placeholder="Select state" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {US_STATES.map((state) => (
                                                <SelectItem key={state} value={state}>{state}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.state && <p className="text-sm text-destructive">{errors.state}</p>}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="city">City</Label>
                                    <Input
                                        id="city"
                                        value={formData.city}
                                        onChange={(e) => handleChange('city', e.target.value)}
                                        placeholder="e.g., Chicago"
                                        data-testid="input-city"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg">Contact Information</CardTitle>
                            <CardDescription>Optional - provide if you have contact details</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="contact_name">Contact Name</Label>
                                    <Input
                                        id="contact_name"
                                        value={formData.contact_name}
                                        onChange={(e) => handleChange('contact_name', e.target.value)}
                                        placeholder="Primary contact's name"
                                        data-testid="input-contact-name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="contact_email">Contact Email</Label>
                                    <Input
                                        id="contact_email"
                                        type="email"
                                        value={formData.contact_email}
                                        onChange={(e) => handleChange('contact_email', e.target.value)}
                                        placeholder="contact@example.org"
                                        data-testid="input-contact-email"
                                    />
                                    {errors.contact_email && <p className="text-sm text-destructive">{errors.contact_email}</p>}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact_phone">Contact Phone</Label>
                                <Input
                                    id="contact_phone"
                                    value={formData.contact_phone}
                                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                                    placeholder="(555) 123-4567"
                                    data-testid="input-contact-phone"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle className="text-lg">Services & Details</CardTitle>
                            <CardDescription>Help us categorize this organization</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="services_offered">Services Offered</Label>
                                <Textarea
                                    id="services_offered"
                                    value={formData.services_offered}
                                    onChange={(e) => handleChange('services_offered', e.target.value)}
                                    placeholder="What specific services does this organization provide?"
                                    rows={3}
                                    data-testid="input-services"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="target_population">Population Served</Label>
                                <Input
                                    id="target_population"
                                    value={formData.target_population}
                                    onChange={(e) => handleChange('target_population', e.target.value)}
                                    placeholder="e.g., Autistic adults, individuals with ADHD, families..."
                                    data-testid="input-population"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="how_found">How did you find this organization?</Label>
                                <Input
                                    id="how_found"
                                    value={formData.how_found}
                                    onChange={(e) => handleChange('how_found', e.target.value)}
                                    placeholder="e.g., Online search, referral, personal experience..."
                                    data-testid="input-how-found"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="other_info">Any other information?</Label>
                                <Textarea
                                    id="other_info"
                                    value={formData.other_info}
                                    onChange={(e) => handleChange('other_info', e.target.value)}
                                    placeholder="Anything else that would be helpful to know..."
                                    rows={2}
                                    data-testid="input-other"
                                />
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex justify-end">
                        <Button type="submit" disabled={submitting} className="gap-2" data-testid="submit-btn">
                            {submitting ? (
                                <>Submitting...</>
                            ) : (
                                <>
                                    Submit Organization
                                    <Send className="h-4 w-4" />
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddOrganizationPage;
